import { getFocusableTreeWalker, useFocusable } from '@react-aria/focus';
import { getInteractionModality, useKeyboard } from '@react-aria/interactions';
import { isCtrlKeyPressed, mergeProps } from '@react-aria/utils';
import type { FocusEventHandler, RefObject } from 'react';
import type { FocusableElement, NativeElementProps } from '../../types.js';
import { type ChildFocusStrategy, useTableFocus } from '../contexts/focus.js';
import { useTableState } from '../contexts/table-state.js';
import { focusTableChild } from '../utils/focus-table-child.js';

export interface UseTable {
  tableProps: NativeElementProps<HTMLTableElement>;
}

export function useTable(ref: RefObject<HTMLTableElement | null>): UseTable {
  const tableFocus = useTableFocus();
  const { rowSelectionMode, onAllRowsSelectionToggle } = useTableState();

  const restoreCellFocus: FocusEventHandler = (e) => {
    if (!tableFocus.focusedRowKey || !tableFocus.focusedColumnKey) {
      // User hasn't interacted with the table yet.
      // Focus the first or last cell, depending on which direction the user is tabbing from.
      const lastFocusedElement = e.relatedTarget as Element;
      const cellElements = e.currentTarget.querySelectorAll('[data-cell-row-key][data-cell-column-key]');
      let cellElementToFocus: Element | undefined;

      if (
        lastFocusedElement &&
        e.currentTarget.compareDocumentPosition(lastFocusedElement) & Node.DOCUMENT_POSITION_FOLLOWING
      ) {
        cellElementToFocus = cellElements[cellElements.length - 1];
      } else {
        cellElementToFocus = cellElements[0];
      }

      const { cellRowKey, cellColumnKey } = (cellElementToFocus as HTMLTableCellElement).dataset;

      // We know the keys are defined, because we queried the DOM for elements that have them
      tableFocus.setFocus(cellRowKey!, cellColumnKey!);
    } else {
      const element = e.currentTarget.querySelector(
        `[data-cell-row-key='${CSS.escape(tableFocus.focusedRowKey)}'][data-cell-column-key='${CSS.escape(tableFocus.focusedColumnKey)}']`,
      ) as HTMLTableCellElement;

      if (element) {
        // If focus is already within the cell, don't refocus
        if (!element.contains(document.activeElement)) {
          focusTableChild(element);
        }
      }
    }
  };

  const { focusableProps } = useFocusable(
    {
      onFocus: (e) => {
        // Ignore focus events bubbling through portals
        if (!e.currentTarget.contains(e.target)) {
          return;
        }

        restoreCellFocus(e);
      },
    },
    ref,
  );

  const { keyboardProps } = useKeyboard({
    onKeyDown: (e) => {
      // Ignore keyboard events bubbling through portals
      if (!e.currentTarget.contains(e.target)) {
        return;
      }

      const focusedCellElement = e.currentTarget.querySelector(
        `[data-cell-row-key='${CSS.escape(tableFocus.focusedRowKey)}'][data-cell-column-key='${CSS.escape(tableFocus.focusedColumnKey)}']`,
      ) as HTMLTableCellElement;

      let cellElementToFocus: Node | null = null;
      let newChildFocusStrategy: ChildFocusStrategy | undefined;

      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        // Focus previous/next cell in the same row
        const selector = `[data-cell-row-key='${CSS.escape(tableFocus.focusedRowKey)}']:not([data-cell-column-key='${CSS.escape(tableFocus.focusedColumnKey)}'])`;
        const walker = getFocusableTreeWalker(e.currentTarget, { accept: (node) => node.matches(selector) });
        walker.currentNode = focusedCellElement;
        cellElementToFocus = e.key === 'ArrowLeft' ? walker.previousNode() : walker.nextNode();
        newChildFocusStrategy = e.key === 'ArrowLeft' ? 'last' : 'first';
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        // Focus previous/next cell in the same column
        const selector = constructColumnSelector(
          e.currentTarget,
          tableFocus.focusedRowKey,
          tableFocus.focusedColumnKey,
        );
        const walker = getFocusableTreeWalker(e.currentTarget, { accept: (node) => node.matches(selector) });
        walker.currentNode = focusedCellElement;
        cellElementToFocus = e.key === 'ArrowUp' ? walker.previousNode() : walker.nextNode();
      } else if (e.key === 'PageUp' || e.key === 'PageDown') {
        // Move focus up/down by 5 rows
        const selector = constructColumnSelector(
          e.currentTarget,
          tableFocus.focusedRowKey,
          tableFocus.focusedColumnKey,
        );
        const walker = getFocusableTreeWalker(e.currentTarget, { accept: (node) => node.matches(selector) });
        walker.currentNode = focusedCellElement;

        const advance = e.key === 'PageUp' ? walker.previousNode.bind(walker) : walker.nextNode.bind(walker);

        // `advance()` will do nothing if there's no appropriate node found, so it's safe to just call it 5 times
        for (let i = 0; i < 5; i++) {
          advance();
        }

        cellElementToFocus = walker.currentNode;
      } else if (e.key === 'Home' || e.key === 'End') {
        // If Ctrl/Cmd is pressed, move focus to the first/last cell of the table;
        // otherwise, move focus to the first/last cell of the current row
        const selector = '[data-cell-row-key][data-cell-column-key]';
        const walker = getFocusableTreeWalker(e.currentTarget, { accept: (node) => node.matches(selector) });
        walker.currentNode = isCtrlKeyPressed(e) ? e.currentTarget : focusedCellElement.parentElement!;
        cellElementToFocus = e.key === 'Home' ? walker.firstChild() : walker.lastChild();
        newChildFocusStrategy = e.key === 'Home' ? 'first' : 'last';
      } else if (e.key === 'Tab') {
        // Table is supposed to be a single Tab stop, but cells may contain elements that are tabbable (checkboxes, buttons, menus, etc.).
        // We don't control the rendering of these elements, so we can't override their tabIndex to prevent tabbing.
        // Instead, we detect when Tab key is pressed, and move focus manually to the guard element before/after the table,
        // so that the browser default behavior will apply starting from that element rather than the currently focused one.
        const tableElement = e.currentTarget as HTMLTableElement;
        const guardElement = e.shiftKey ? tableElement.previousElementSibling : tableElement.nextElementSibling;

        if (guardElement) {
          focusTableChild(guardElement as FocusableElement);
        }

        return;
      } else if (e.key === 'a' && isCtrlKeyPressed(e) && rowSelectionMode === 'multiple') {
        // Select all rows
        onAllRowsSelectionToggle?.(true);
      } else {
        // if we didn't handle anything, return early so we don't preventDefault
        return;
      }

      if (cellElementToFocus) {
        const { cellRowKey, cellColumnKey } = (cellElementToFocus as HTMLTableCellElement).dataset;

        // We know the keys are defined, because we queried the DOM for elements that have them
        tableFocus.setFocus(cellRowKey!, cellColumnKey!, newChildFocusStrategy);
      }

      e.preventDefault();
    },
  });

  const customProps: NativeElementProps<HTMLTableElement> = {
    role: 'grid',
    // If no cell is tabbable within the table, make the table itself tabbable,
    // otherwise the Tab key will just skip it
    tabIndex: tableFocus.focusedRowKey && tableFocus.focusedColumnKey ? -1 : 0,
    onFocusCapture: (e) => {
      // If we're navigating into the table with a keyboard, focus the last focused cell instead of the first tabbable element
      if (
        getInteractionModality() === 'keyboard' &&
        !e.currentTarget.contains(e.relatedTarget) &&
        e.currentTarget.contains(e.target)
      ) {
        restoreCellFocus(e);
      }
    },
  };

  return {
    tableProps: mergeProps(focusableProps, keyboardProps, customProps),
  };
}

/** Constructs a DOM selector for cells in the same column, taking `colspan` into account. */
function constructColumnSelector(tableElement: HTMLTableElement, rowKey: string, columnKey: string): string {
  const baseSelector = `[data-cell-column-key='${CSS.escape(columnKey)}']:not([data-cell-row-key='${CSS.escape(rowKey)}'])`;
  const columnHeader = tableElement.querySelector(
    `th[data-cell-row-key='${CSS.escape(rowKey)}'][data-cell-column-key='${CSS.escape(columnKey)}']`,
  );

  const columnSelectors: string[] = [baseSelector];

  let currentColumn = columnHeader?.previousElementSibling;

  while (currentColumn) {
    const { cellColumnKey } = (currentColumn as HTMLElement).dataset;

    if (cellColumnKey) {
      columnSelectors.push(
        `[data-cell-column-key='${CSS.escape(cellColumnKey)}'][colspan]:not([data-cell-row-key='${CSS.escape(rowKey)}'])`,
      );
    }

    currentColumn = currentColumn.previousElementSibling;
  }

  return `:is(${columnSelectors.join(', ')})`;
}
